import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { nextTick } from 'vue';
import type { Anchor } from 'vuetify';
import useUserService from '@/composables/apiServices/useUserService';
import { waitForIt } from '@/helpers/utils';
import { ROUTE_NAMES } from '@/router/conf';

export interface IOnBoardingStep {
  title: string;
  text: string;
  version: number;
  icon?: string;
  targetSelector?: string;
  location?: Anchor;
  origin?: Anchor | 'auto' | 'overlap';
  before?: () => Promise<void>;
  after?: () => Promise<void>;
}

// Module-level singleton — shared across all useOnboarding() call sites
export const isOnboardingActive = ref(false);
export const isWhatsNewMode = ref(false);
const _toggleQuickSettings = ref<((open: boolean) => void) | null>(null);

export const registerToggleQuickSettings = (fn: (open: boolean) => void) => {
  _toggleQuickSettings.value = fn;
};
const _toggleSearchContainer = ref<((open: boolean) => void) | null>(null);

export const registerToggleSearchContainer = (fn: (open: boolean) => void) => {
  _toggleSearchContainer.value = fn;
};

export const useOnboarding = async () => {
  const { t } = useI18n();
  const router = useRouter();
  const { details, updateDetails } = useUserService();
  const { data: userDetails } = details();
  const { mutateAsync: updateUserDetails, isPending } = updateDetails();

  const navigateTo = async (name: string) => {
    if (router.currentRoute.value.name !== name) {
      await router.push({ name });
      await nextTick();
    }
  };

  const waitForElement = async <T extends Element>(selector: string): Promise<T> => {
    await waitForIt(async () => !!document.querySelector<T>(selector));
    return document.querySelector<T>(selector)!;
  };
  const waitForElementToHide = async (selector: string) => {
    // Small delay to ensure any transitions have started
    await new Promise((resolve) => setTimeout(resolve, 250));
    // If the element is already gone, resolve immediately
    if (!document.querySelector(selector)) return;
    // Wait for the element to disappear
    await waitForIt(async () => !document.querySelector(selector));
  };

  const showNewAccountDialog = computed(() => userDetails.value?.meta.initialVersion === 3);
  const storedVersion = computed(() => {
    const raw = userDetails.value?.meta.onBoardingCompleted;
    if (raw === null || raw === undefined) return null;
    return Number(raw);
  });
  const onBoardingSteps = computed((): IOnBoardingStep[] =>
    [
      {
        title: t('onBoarding.steps.folders.title'),
        text: t('onBoarding.steps.folders.text'),
        icon: 'FoldersList',
        version: 385,
        targetSelector: '[data-onboarding="folder-list"]',
        before: async () => {
          await navigateTo(ROUTE_NAMES.BASE_LAYOUT_TAB);
          await waitForElement('[data-onboarding="folder-list"]');
        }
      },
      {
        title: t('onBoarding.steps.quickSettings.title'),
        text: t('onBoarding.steps.quickSettings.text'),
        icon: 'QuickSettings',
        version: 385,
        targetSelector: '[data-onboarding="quick-settings-view-all"]',
        before: async () => {
          _toggleQuickSettings.value?.(true);
          await waitForElement('[data-onboarding="quick-settings-drawer"]');
          await new Promise<void>((resolve) => {
            setTimeout(() => {
              resolve();
            }, 250);
          });
        },
        after: async () => {
          _toggleQuickSettings.value?.(false);
        }
      },
      {
        title: t('onBoarding.steps.allSettings.title'),
        text: t('onBoarding.steps.allSettings.text'),
        icon: 'AllSettings',
        version: 385,
        targetSelector: '[data-onboarding="settings-sidebar"]',
        before: async () => {
          await navigateTo(ROUTE_NAMES.SETTINGS_GENERAL);
          await waitForElement('[data-onboarding="settings-sidebar"]');
        }
      },
      {
        title: t('onBoarding.steps.security.title'),
        text: t('onBoarding.steps.security.text'),
        icon: 'SecuritySettings',
        version: 385,
        targetSelector: '[data-onboarding="totp"]',
        location: 'top center' as Anchor,
        origin: 'bottom center' as Anchor,
        before: async () => {
          await navigateTo(ROUTE_NAMES.SECURITY_2FA);
          await waitForElement('[data-onboarding="totp"]');
        }
      },
      {
        title: t('onBoarding.steps.generalThreadingBehavior.title'),
        text: t('onBoarding.steps.generalThreadingBehavior.text'),
        version: 386,
        targetSelector: '[data-onboarding="general-threading-behavior"]',
        location: 'top center' as Anchor,
        origin: 'bottom center' as Anchor,
        before: async () => {
          await navigateTo(ROUTE_NAMES.SETTINGS_GENERAL);
          await waitForElement('[data-onboarding="general-threading-behavior"]');
        }
      },
      {
        title: t('onBoarding.steps.generalEditorBehavior.title'),
        text: t('onBoarding.steps.generalEditorBehavior.text'),
        version: 386,
        targetSelector: '[data-onboarding="general-editor-behavior"]',
        location: 'top center' as Anchor,
        origin: 'bottom center' as Anchor,
        before: async () => {
          await navigateTo(ROUTE_NAMES.SETTINGS_GENERAL);
          await waitForElement('[data-onboarding="general-editor-behavior"]');
        }
      },
      {
        title: t('onBoarding.steps.appearanceAdditionalSettings.title'),
        text: t('onBoarding.steps.appearanceAdditionalSettings.text'),
        version: 386,
        targetSelector: '[data-onboarding="appearance-additional-settings"]',
        location: 'top center' as Anchor,
        origin: 'bottom center' as Anchor,
        before: async () => {
          await navigateTo(ROUTE_NAMES.SETTINGS_APPEARANCE);
          await waitForElement('[data-onboarding="appearance-additional-settings"]');
        }
      },
      {
        title: t('onBoarding.steps.search.title'),
        text: t('onBoarding.steps.search.text'),
        version: 392,
        targetSelector: '[data-onboarding="search-instructions-open-button"]',
        before: async () => {
          await navigateTo(ROUTE_NAMES.BASE_LAYOUT);
          await waitForElement<HTMLInputElement>('[data-onboarding="search-input"]');
          _toggleSearchContainer.value?.(true);
          await waitForElementToHide('[data-onboarding="search-loading"]');
          await waitForElement<HTMLButtonElement>(
            '[data-onboarding="search-instructions-open-button"]'
          );
        },
        after: async () => {
          _toggleSearchContainer.value?.(false);
        }
      },
      {
        title: t('onBoarding.steps.3PanelBottom.title'),
        text: t('onBoarding.steps.3PanelBottom.text'),
        version: 3108,
        targetSelector: '[data-onboarding="quick-settings-3-panel-bottom"]',
        before: async () => {
          _toggleQuickSettings.value?.(true);
          await waitForElement('[data-onboarding="quick-settings-drawer"]');
          await new Promise<void>((resolve) => {
            setTimeout(() => {
              resolve();
            }, 250);
          });
        },
        after: async () => {
          _toggleQuickSettings.value?.(false);
        }
      }
    ].filter((step) => step.version > (storedVersion.value ?? 0))
  );

  const markOnboardingComplete = async () => {
    const maxVersion = Math.max(...onBoardingSteps.value.map((step) => step.version));
    await updateUserDetails({ onBoardingCompleted: maxVersion });
  };

  const isWhatsNewAvailable = computed(() => {
    return (
      storedVersion.value !== null && storedVersion.value >= 385 && onBoardingSteps.value.length > 0
    );
  });

  const startWhatsNew = () => {
    isWhatsNewMode.value = true;
    isOnboardingActive.value = true;
  };

  return {
    showNewAccountDialog,
    onBoardingSteps,
    markOnboardingComplete,
    isPending,
    isWhatsNewAvailable,
    startWhatsNew
  };
};
